/**
 * qrLabelPdf.ts
 *
 * Generates a client-side A4 portrait PDF of laptop QR labels.
 *
 * Layout: 6 columns x 8 rows = 48 labels per page
 * Cell:   32mm wide x 34mm tall
 * QR:     24mm x 24mm (centered)
 * QR payload: AKT1:L:<laptop-uuid>
 * Printed text lines:
 *   Line 1: stock_id  (e.g. LAP-000054)
 *   Line 2: serial_number (optional, grey, smaller)
 * Cutting guides: fine grey borders around each cell
 */
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

export interface LaptopLabelInput {
  id: string;
  stock_id: string;
  serial_number?: string | null;
}

// A4 portrait dimensions in mm
const PAGE_W = 210;
const PAGE_H = 297;

// Label grid
const COLS = 6;
const ROWS = 8;
const LABELS_PER_PAGE = COLS * ROWS;
const MARGIN_H = 5; // left/right margin
const MARGIN_V = 5; // top/bottom margin
const CELL_W = (PAGE_W - 2 * MARGIN_H) / COLS; // ~33.33mm
const CELL_H = (PAGE_H - 2 * MARGIN_V) / ROWS; // ~35.875mm
const QR_SIZE = 24; // mm
const TEXT_FONT_SIZE = 7;   // stock_id
const SERIAL_FONT_SIZE = 5; // serial_number

async function laptopToQrDataUrl(laptop: LaptopLabelInput): Promise<string> {
  const payload = `AKT1:L:${laptop.id}`;
  return QRCode.toDataURL(payload, {
    errorCorrectionLevel: 'M',
    margin: 0,
    width: 256,
  });
}

export async function generateLaptopQrLabelPdf(laptops: LaptopLabelInput[]): Promise<void> {
  if (laptops.length === 0) return;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  pdf.setFont('helvetica');

  for (let i = 0; i < laptops.length; i++) {
    if (i > 0 && i % LABELS_PER_PAGE === 0) {
      pdf.addPage();
    }

    const laptop = laptops[i];
    const pageIndex = i % LABELS_PER_PAGE;
    const col = pageIndex % COLS;
    const row = Math.floor(pageIndex / COLS);

    const cellX = MARGIN_H + col * CELL_W;
    const cellY = MARGIN_V + row * CELL_H;

    // Draw cell border (cutting guide)
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.15);
    pdf.rect(cellX, cellY, CELL_W, CELL_H);

    // QR code (centered horizontally in cell)
    const qrX = cellX + (CELL_W - QR_SIZE) / 2;
    const qrY = cellY + 2; // 2mm top padding
    try {
      const dataUrl = await laptopToQrDataUrl(laptop);
      pdf.addImage(dataUrl, 'PNG', qrX, qrY, QR_SIZE, QR_SIZE);
    } catch {
      // Fallback: draw placeholder
      pdf.setFillColor(240, 240, 240);
      pdf.rect(qrX, qrY, QR_SIZE, QR_SIZE, 'F');
    }

    // Stock ID text
    pdf.setFontSize(TEXT_FONT_SIZE);
    pdf.setTextColor(20, 20, 20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(laptop.stock_id, cellX + CELL_W / 2, qrY + QR_SIZE + 3.5, { align: 'center' });

    // Serial number (optional, smaller, grey)
    if (laptop.serial_number) {
      pdf.setFontSize(SERIAL_FONT_SIZE);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      const serial = laptop.serial_number.length > 18
        ? laptop.serial_number.slice(0, 17) + '…'
        : laptop.serial_number;
      pdf.text(serial, cellX + CELL_W / 2, qrY + QR_SIZE + 7, { align: 'center' });
    }
  }

  const filename = `laptop_qr_labels_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(filename);
}
